import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import SearchBar from 'shared/components/search-bar/search-bar';
import FilterChip from 'shared/components/table-filter-chips/filter-chip/filter-chip';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import MultiValueSearchFilterChip from 'shared/components/table-filter-chips/multi-value-search-filter-chip/multi-value-search-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';

import { FilterValueType } from 'features/residents/models/resident-settings-filter-value';
import useResidentSettingsFilters from 'features/residents/resident-settings/components/resident-settings-filters/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './resident-settings-filters.module.scss';

const ResidentSettingsFilters = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    filtersState: {
      searchBar,
      organizationUnits,
      navigationParentOrganisationUnitId,
      selectOptions,
      changeSearchBarValue,
      searchBarConfirmationGuard,
      changeSelectedOrganizationUnits,
      clearFilters,
    },
  } = use(ResidentSettingsProviderContext);

  const {
    scenarioFilterOptions,
    selectedScenarioFilterOptions,
    linkedDeviceFilterOptions,
    initialSelectedFilterOption,
    shouldShowWanderingDetectionFilter,
    wanderingBoundaryFilterOptions,
    selectedWanderingBoundaryFilterOptions,
    scenariosMutedFilterOptions,
    initialSelectedScenariosMutedFilterOption,
  } = useResidentSettingsFilters();

  return (
    <div className={styles.residentFilters}>
      <SearchBar onSearchValueChanged={changeSearchBarValue} searchValue={searchBar} changeLocalValueFunc={searchBarConfirmationGuard} />
      <OrganisationUnitFilterChip
        onOptionsSelected={changeSelectedOrganizationUnits}
        selectedOptions={organizationUnits}
        placeHolder={t('filters.organisationUnit')}
        initialVisibleOrganisationUnitId={navigationParentOrganisationUnitId}
      />
      <MultiValueFilterChip
        options={scenarioFilterOptions}
        onOptionsSelected={(keys: string[]) => selectOptions(FilterValueType.Scenario, keys)}
        placeHolder={t('filters.scenario')}
        selectedOptions={selectedScenarioFilterOptions}
      />
      {shouldShowWanderingDetectionFilter && (
        <MultiValueSearchFilterChip
          options={wanderingBoundaryFilterOptions ?? []}
          onOptionsSelected={(keys: string[]) => selectOptions(FilterValueType.WanderingBoundary, keys)}
          placeHolder={t('filters.wanderingDetection')}
          selectedOptions={selectedWanderingBoundaryFilterOptions ?? []}
        />
      )}
      <FilterChip
        options={linkedDeviceFilterOptions}
        onOptionSelected={(key) => selectOptions(FilterValueType.LinkedDevice, key ? [key] : [])}
        placeHolder={t('filters.linkedDevice')}
        initialSelectedOption={initialSelectedFilterOption}
      />
      <FilterChip
        options={scenariosMutedFilterOptions}
        onOptionSelected={(key) => selectOptions(FilterValueType.ScenariosMuted, key ? [key] : [])}
        placeHolder={t('filters.scenariosMuted')}
        initialSelectedOption={initialSelectedScenariosMutedFilterOption}
      />
      <div className={styles.clearFilters}>
        <Button variant="text" color="primary" onClick={clearFilters}>
          {t('filters.clearFilters')}
        </Button>
      </div>
    </div>
  );
};

export default ResidentSettingsFilters;
